import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TYPES = ["Residential", "Commercial", "Interior", "Landscape", "Other"];
const EMPTY = { name: "", email: "", phone: "", project_type: "", message: "" };

const Field = ({ label, children }) => (
  <label className="field block relative">
    <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-stone-500">{label}</span>
    {children}
    <span aria-hidden="true" className="field-line" />
  </label>
);

export const EnquiryForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    const saved = sessionStorage.getItem("carvings:prefill");
    if (saved) {
      sessionStorage.removeItem("carvings:prefill");
      setForm((f) => ({ ...f, ...JSON.parse(saved) }));
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      setSent(true);
      setForm(EMPTY);
      toast.success("Thank you — we'll be in touch soon.");
    } catch (err) {
      toast.error("Could not send your enquiry. Please check your details or email us directly.");
    } finally {
      setLoading(false);
    }
  };

  const input = "mt-2 w-full bg-transparent border-b border-stone-300 py-3 text-stone-900 placeholder:text-stone-400 focus:outline-none focus:border-stone-900 transition-colors";

  return (
    <form onSubmit={submit} className="space-y-8" data-testid="enquiry-form">
      <div className="grid sm:grid-cols-2 gap-8">
        <Field label="Name *"><input required value={form.name} onChange={set("name")} className={input} placeholder="Your full name" data-testid="enquiry-name-input" /></Field>
        <Field label="Email *"><input required type="email" value={form.email} onChange={set("email")} className={input} placeholder="you@email.com" data-testid="enquiry-email-input" /></Field>
        <Field label="Phone"><input value={form.phone} onChange={set("phone")} className={input} placeholder="+91" data-testid="enquiry-phone-input" /></Field>
        <Field label="Project type">
          <select value={form.project_type} onChange={set("project_type")} className={input} data-testid="enquiry-type-select">
            <option value="">Select</option>
            {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </Field>
      </div>
      <Field label="Tell us about your project *">
        <textarea required rows={4} value={form.message} onChange={set("message")} className={`${input} resize-none`} placeholder="Site location, size, timeline, ideas…" data-testid="enquiry-message-input" />
      </Field>
      <div className="flex flex-wrap items-center gap-6">
        <motion.button whileTap={{ scale: 0.96 }} type="submit" disabled={loading} className="btn-dark disabled:opacity-60" data-testid="enquiry-submit-btn">
          {loading ? <span className="blocks" aria-label="Sending"><i /><i /><i /></span> : <>Send enquiry <ArrowRight size={16} /></>}
        </motion.button>
        <AnimatePresence>
          {sent && (
            <motion.p initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }} className="flex items-center gap-3 text-sm text-[#2D6A4F]" data-testid="enquiry-success-msg">
              <span className="blocks built" aria-hidden="true"><i /><i /><i /></span> Enquiry received. We'll reply within a few days.
            </motion.p>
          )}
        </AnimatePresence>
      </div>
    </form>
  );
};
